import { RegisteredVehicle } from "common/graphql";
import { NewVehicleRow, VehicleRowErrors } from "./vehicles.types";

const PLATE_REGEX = /^[A-Za-z0-9]{3,9}$/;

export interface ValidationResult {
  errors: VehicleRowErrors;
  hasErrors: boolean;
}

/**
 * Validates a single new vehicle row against format rules, within-batch duplicates,
 * and existing registered vehicles.
 *
 * @param row - The row being validated
 * @param otherNewRows - All other new rows in the batch (excluding this row)
 * @param existingVehicles - Already-registered vehicles for the account
 */
export function validateVehicleRow(
  row: NewVehicleRow,
  otherNewRows: NewVehicleRow[],
  existingVehicles: RegisteredVehicle[],
): ValidationResult {
  const errors: VehicleRowErrors = {};

  if (!row.stateCode) {
    errors.stateCode = "State is required";
  }

  if (!row.licensePlate) {
    errors.licensePlate = "License plate is required";
  } else if (!PLATE_REGEX.test(row.licensePlate)) {
    errors.licensePlate = "3–9 alphanumeric characters, no spaces or special characters";
  }

  if (!row.type) {
    errors.type = "Vehicle type is required";
  }

  const isDuplicateInBatch = otherNewRows.some(
    (other) =>
      other.licensePlate.toUpperCase() === row.licensePlate.toUpperCase() &&
      other.stateCode.toUpperCase() === row.stateCode.toUpperCase(),
  );

  const isDuplicateOfExisting = existingVehicles.some(
    (existing) =>
      existing.licensePlate.toUpperCase() === row.licensePlate.toUpperCase() &&
      existing.stateCode.toUpperCase() === row.stateCode.toUpperCase(),
  );

  if (isDuplicateInBatch || isDuplicateOfExisting) {
    // Only set if there's no format error already — format errors take priority
    errors.licensePlate = errors.licensePlate ?? "This plate is already registered for this state";
  }

  return { errors, hasErrors: Object.keys(errors).length > 0 };
}
